import { inject, Injectable } from '@angular/core';

import { httpsCallable, HttpsCallableOptions } from 'firebase/functions';
import { LogService } from 'src/app/logging';

import { Client } from '../../data/models/clients.model';
import { FirebaseService } from '../../shared/services/firebase.service';
import { FunctionsHttpError, FunctionsHttpErrorModel } from '../models/functions-error.model';

type FunctionsResponse<T> = T | FunctionsHttpErrorModel;

function isError<T>(resp: FunctionsResponse<T>): resp is FunctionsHttpErrorModel {
	return (
		(resp as FunctionsHttpErrorModel)?.code !== undefined &&
		(resp as FunctionsHttpErrorModel)?.httpErrorCode !== undefined
	);
}

@Injectable({ providedIn: 'root' })
export class FunctionsService {
	private readonly functions = inject(FirebaseService).functions;

	constructor(private log: LogService) {}

	async callFunction<Ti, To>(name: string, data: Ti, options?: HttpsCallableOptions): Promise<To> {
		const callable = httpsCallable<Ti, FunctionsResponse<To>>(this.functions, name, options);
		let response: FunctionsResponse<To>;

		try {
			response = (await callable(data)).data;
			if (isError(response)) {
				throw new FunctionsHttpError(response);
			}
		} catch (err) {
			this.log.error(`Failed to execute ${name}`, {}, err as Error);
			throw err;
		}

		return response;
	}

	async sendWelcomeMessage(data: { id: string; clientId: string }): Promise<void> {
		await this.callFunction('sendWelcomeMessage', data);
	}

	async addClient(id: string, client: Client): Promise<void> {
		await this.callFunction('addClient', { id, client });
	}

	userIdFromEmail(email: 'ping'): void;
	async userIdFromEmail(email: string): Promise<string | undefined>;
	async userIdFromEmail(email: string): Promise<string | undefined> {
		const call = this.callFunction<{ email: string }, string | undefined>('userIdFromEmail', { email });
		if (email !== 'ping') {
			return call;
		}
	}
}
